import type { Locale, MessageKey } from "@src/i18n/locale-catalog";
import type { Translate } from "@src/i18n/translate";
import type { AppDialog } from "./AppDialogs";
import { DiagnosticBadge } from "@src/features/equation-editor/components/cards/DiagnosticField";
import type { RefObject } from "react";
import type { TransformationViewportHandle } from "@src/features/visualization/TransformationViewport";
import { diagnosticMessage } from "@src/features/equation-editor/lib/diagnostic-message";
import { localeMetadata } from "@src/i18n/locale-catalog";
import { selectCanShare } from "../state/app-state";
import { useAppStore } from "../hooks/use-app-store";
import { useAppStoreApi } from "../state/app-store-context";

export function AppControls({
  locale,
  t,
  scene,
  shareStatus,
  isSharing,
  onChangeLocale,
  onShare,
  onDialog,
}: {
  locale: Locale;
  t: Translate;
  scene: RefObject<TransformationViewportHandle | null>;
  shareStatus: MessageKey | null;
  isSharing: boolean;
  onChangeLocale: (locale: Locale) => void;
  onShare: (invoker: HTMLButtonElement) => void;
  onDialog: (dialog: AppDialog, invoker: HTMLButtonElement) => void;
}) {
  const store = useAppStoreApi();
  const activeDimension = useAppStore((state) => state.activeDimension);
  const evaluation2 = useAppStore((state) => state.equations[2].evaluation);
  const evaluation3 = useAppStore((state) => state.equations[3].evaluation);
  const dimensionDiagnostics = {
    2: evaluation2.kind === "stale" ? evaluation2.diagnostics : [],
    3: evaluation3.kind === "stale" ? evaluation3.diagnostics : [],
  };
  const canShare = useAppStore(selectCanShare);
  return (
    <>
      <div className="mode-switch" role="group" aria-label={t("dimension")}>
        {([2, 3] as const).map((dimension) => {
          const messages = [
            ...new Set(
              dimensionDiagnostics[dimension].map((diagnostic) =>
                diagnosticMessage(t, diagnostic.code),
              ),
            ),
          ];
          return (
            <span className="mode-switch-option" key={dimension}>
              <button
                type="button"
                aria-pressed={activeDimension === dimension}
                onClick={() => {
                  store.getState().setDimension(dimension);
                }}
              >
                {dimension}D
              </button>
              {activeDimension !== dimension && messages.length > 0 && (
                <DiagnosticBadge
                  id={`dimension-${String(dimension)}`}
                  messages={messages}
                  indicatorLabel={t("dimensionInputErrors", {
                    dimension: `${String(dimension)}D`,
                  })}
                />
              )}
            </span>
          );
        })}
      </div>
      <div className="view-tools" role="group" aria-label={t("appControls")}>
        <select
          name="language"
          aria-label={t("language")}
          value={locale}
          onChange={(event) => {
            onChangeLocale(event.target.value as Locale);
          }}
        >
          {localeMetadata.map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => scene.current?.resetView(activeDimension)}
        >
          {t("resetView")}
        </button>
        <button
          type="button"
          onClick={(event) => {
            onDialog("reset", event.currentTarget);
          }}
          aria-haspopup="dialog"
        >
          {t("resetWorkspace")}
        </button>
        <button
          type="button"
          onClick={(event) => {
            onShare(event.currentTarget);
          }}
          aria-haspopup="dialog"
          disabled={isSharing || !canShare}
          title={!canShare ? t("shareRequiresValidWorkspace") : undefined}
        >
          {t(shareStatus ?? "share")}
        </button>
        <button
          type="button"
          onClick={(event) => {
            onDialog("about", event.currentTarget);
          }}
          aria-haspopup="dialog"
        >
          {t("about")}
        </button>
      </div>
    </>
  );
}
